import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  VStack,
  useTheme,
} from '@frontend/components';

import { PageTitle } from 'components/PageTitle';

import { getAvailableGroups, getUser } from 'store/session/selectors';

import { useSearchParams } from 'utils/hooks';

import { CreateQuoteButton } from './components/CreateQuoteButton';
import { UserGroupFilter } from './components/UserGroupFilter';
import { AssignedToYouPane } from './panes/AssignedToYouPane';
import { InternalQuotesPane } from './panes/InternalQuotesPane';
import { OrdersPane } from './panes/OrdersPane';
import { QuotesOverviewPane } from './panes/QuotesOverviewPane';
import { TeamAssignmentsPane } from './panes/TeamAssignmentsPane';

const getTimeOfDay = () => {
  const now = new Date();
  const hours = now.getHours();
  return hours < 12 ? 'morning' : hours < 18 ? 'afternoon' : 'evening';
};

const useTimeOfDay = () => {
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay());
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeOfDay(getTimeOfDay());
    }, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return timeOfDay;
};

const GRID_ITEM_STYLES = {
  height: 'full',
  maxHeight: { base: '70dvh', desktop: 'unset' },
};

export const MultiSiteDashboard = () => {
  const theme = useTheme();
  const timeOfDay = useTimeOfDay();
  const user = useSelector(getUser);
  const availableGroupIds = useSelector(getAvailableGroups);

  const [{ groups }, setSearchParams] = useSearchParams<{ groups: number[] }>();

  const activeGroupIds = groups?.split(',')?.map(Number) ?? availableGroupIds;
  const setActiveGroupIds = useCallback(
    (nextActiveGroupIds: number[]) => {
      const allGroupsSelected = availableGroupIds.every((id) =>
        nextActiveGroupIds.includes(id),
      );
      setSearchParams({
        groups: allGroupsSelected ? undefined : nextActiveGroupIds,
      });
    },
    [setSearchParams, availableGroupIds],
  );

  return (
    <VStack align="stretch" padding={3} gap={2} minHeight="100%">
      <PageTitle title="🥷 Multi-Site Dashboard" />
      <Flex
        as={Heading}
        size="xl"
        justifyContent="space-between"
        flexDirection={{ base: 'column', tablet: 'row' }}
        alignItems="center"
        gap={1.5}
      >
        <Flex
          gap={{ base: 1, tablet: 1.5 }}
          flexDirection={{ base: 'column', tablet: 'row' }}
        >
          <Box as="span">
            Good {timeOfDay}, {user?.firstName}
          </Box>
          <UserGroupFilter
            activeGroupIds={activeGroupIds}
            onApply={setActiveGroupIds}
          />
        </Flex>
        <CreateQuoteButton />
      </Flex>
      <Grid
        flex={1}
        templateAreas={{
          base: `
            "quotes"
            "internal-quotes"
            "assigned-to-you"
            "orders"
            "team-assignments"
          `,
          tablet: `
            "quotes quotes"
            "internal-quotes assigned-to-you"
            "orders team-assignments"
          `,
          desktop: `
            "quotes quotes assigned-to-you"
            "orders internal-quotes assigned-to-you"
            "team-assignments internal-quotes assigned-to-you"
          `,
          desktopLarge: `
            "quotes quotes internal-quotes assigned-to-you"
            "orders team-assignments internal-quotes assigned-to-you"
          `,
        }}
        templateColumns={{
          base: 'minmax(0, 1fr)',
          tablet: 'repeat(2, minmax(0, 1fr))',
          desktop: `repeat(3, minmax(0, 1fr))`,
          desktopLarge: `repeat(4, minmax(0, 1fr))`,
          desktopXLarge: `minmax(0, 1fr) minmax(0, 1fr) minmax(0, ${theme.space[60]}) minmax(0, ${theme.space[60]})`,
        }}
        templateRows={{
          base: 'repeat(5, minmax(0, auto))',
          tablet: 'min-content min-content minmax(0, auto)',
          desktopLarge: 'min-content minmax(0, auto)',
        }}
        gap={2}
      >
        <GridItem gridArea="quotes" {...GRID_ITEM_STYLES}>
          <QuotesOverviewPane activeGroupIds={activeGroupIds} />
        </GridItem>
        <GridItem
          gridArea="internal-quotes"
          {...GRID_ITEM_STYLES}
          minHeight={{ desktop: 0 }}
          sx={{
            [`@media (min-width: ${theme.breakpoints.desktop})`]: {
              contain: 'size',
            },
          }}
        >
          <InternalQuotesPane activeGroupIds={activeGroupIds} />
        </GridItem>
        <GridItem
          gridArea="assigned-to-you"
          {...GRID_ITEM_STYLES}
          minHeight={{ desktop: 0 }}
          sx={{
            [`@media (min-width: ${theme.breakpoints.desktop})`]: {
              contain: 'size',
            },
          }}
        >
          <AssignedToYouPane activeGroupIds={activeGroupIds} />
        </GridItem>
        <GridItem gridArea="orders" {...GRID_ITEM_STYLES}>
          <OrdersPane activeGroupIds={activeGroupIds} />
        </GridItem>
        <GridItem gridArea="team-assignments" {...GRID_ITEM_STYLES}>
          <TeamAssignmentsPane activeGroupIds={activeGroupIds} />
        </GridItem>
      </Grid>
    </VStack>
  );
};
